/**
 * Performance Analytics – Static page with dummy data.
 * Follows the same layout as SlashPage-style analytics (KPI cards, line chart, pie chart)
 * but uses the existing dashboard theme (light, --dash-* variables).
 */
import React from 'react';
import { Area, AreaChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import '../styles/dashboard.css';

// Dummy data – static, no API
const KPI_DATA = [
  { label: 'Total Views', value: '45.2K', trend: 'up' },
  { label: 'Link Clicks', value: '12.8K', trend: 'up' },
  { label: 'Sales', value: '350', trend: 'up' },
];

const VIEWS_OVER_30_DAYS = [
  { date: 'Jan 12', views: 1200 },
  { date: 'Jan 15', views: 1350 },
  { date: 'Jan 18', views: 1100 },
  { date: 'Jan 21', views: 1650 },
  { date: 'Jan 24', views: 1480 },
  { date: 'Jan 27', views: 1920 },
  { date: 'Jan 30', views: 1750 },
  { date: 'Feb 2', views: 2100 },
  { date: 'Feb 5', views: 1980 },
  { date: 'Feb 8', views: 2350 },
];

const TRAFFIC_SOURCES = [
  { name: 'Instagram', value: 45, color: '#8B5CF6' },
  { name: 'TikTok', value: 30, color: '#059669' },
  { name: 'Direct', value: 15, color: '#0EA5E9' },
  { name: 'Other', value: 10, color: '#94A3B8' },
];

// Solid green upward arrow: sharp triangle + short narrow stem (stat card reference)
const UpArrowIcon = () => (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
    <path d="M6 1L10 5H7V11H5V5H2L6 1Z" fill="#059669" />
  </svg>
);

function KpiTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="dashboard-chart-tooltip" style={{ padding: '8px 12px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #E2E8F0' }}>
      <div className="tooltip-date">{d.date}</div>
      <div className="tooltip-row"><span>Views: <strong>{d.views?.toLocaleString()}</strong></span></div>
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const total = TRAFFIC_SOURCES.reduce((s, t) => s + t.value, 0);
  const pct = p.value ? ((p.value / total) * 100).toFixed(1) : 0;
  return (
    <div className="dashboard-chart-tooltip" style={{ padding: '8px 12px', background: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #E2E8F0' }}>
      <div className="tooltip-row"><span>{p.name}: <strong>{pct}%</strong></span></div>
    </div>
  );
}

export default function PerformanceAnalytics() {
  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--dash-text)', margin: 0 }}>
          Performance Analytics
        </h1>
      </div>

      <div
        className="dashboard-main perf-analytics-grid"
        style={{
          padding: '2rem 2.4rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gridTemplateRows: 'auto auto',
          gap: '1.2rem',
        }}
      >
        {/* Row 1: 3 columns */}
        {KPI_DATA.map((kpi) => (
          <div
            key={kpi.label}
            className="dashboard-kpi-card-wrapper"
            style={{
              background: 'var(--dash-card)',
              borderRadius: '12px',
              padding: '1.6rem 1.8rem',
              boxShadow: 'var(--dash-shadow)',
              border: '1px solid var(--dash-border-light)',
              minHeight: '100px',
            }}
          >
            <div style={{ fontSize: '1.3rem', color: 'var(--dash-text-muted)', fontWeight: '500', marginBottom: '0.5rem' }}>
              {kpi.label}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap' }}>
              <span style={{ fontSize: '2.2rem', fontWeight: '700', color: 'var(--dash-text)' }}>{kpi.value}</span>
              {kpi.trend === 'up' && (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <UpArrowIcon />
                </span>
              )}
            </div>
          </div>
        ))}

        {/* Row 2: 2 columns – line chart (span 2), pie chart (span 1) */}
        <div
          className="perf-analytics-chart-line"
          style={{
            gridColumn: '1 / 3',
            background: 'var(--dash-card)',
            borderRadius: 'var(--dash-radius)',
            padding: '1.6rem',
            boxShadow: 'var(--dash-shadow)',
            border: '1px solid var(--dash-border-light)',
            minHeight: '320px',
          }}
        >
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--dash-text)', margin: '0 0 1.6rem 0' }}>
              Views over Last 30 Days
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={VIEWS_OVER_30_DAYS} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--dash-green)" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="var(--dash-green)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--dash-border-light)" />
                <XAxis dataKey="date" stroke="var(--dash-text-muted)" fontSize={12} tickLine={false} />
                <YAxis stroke="var(--dash-text-muted)" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                <Tooltip content={<KpiTooltip />} />
                <Area type="monotone" dataKey="views" stroke="var(--dash-green)" strokeWidth={2} fill="url(#viewsGradient)" />
                <Line type="monotone" dataKey="views" stroke="var(--dash-green)" strokeWidth={2} dot={false} activeDot={{ r: 4, fill: 'var(--dash-green)' }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

        <div
          className="perf-analytics-chart-pie"
          style={{
            gridColumn: '3 / 4',
            background: 'var(--dash-card)',
            borderRadius: 'var(--dash-radius)',
            padding: '1.6rem',
            boxShadow: 'var(--dash-shadow)',
            border: '1px solid var(--dash-border-light)',
            minHeight: '320px',
          }}
        >
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--dash-text)', margin: '0 0 1.6rem 0' }}>
              Traffic Sources
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={TRAFFIC_SOURCES}
                  cx="50%"
                  cy="50%"
                  outerRadius={95}
                  dataKey="value"
                >
                  {TRAFFIC_SOURCES.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.2rem 2rem', justifyContent: 'center', marginTop: '-0.8rem' }}>
              {TRAFFIC_SOURCES.map((s) => (
                <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
                  <span style={{ fontSize: '1.3rem', color: 'var(--dash-text-soft)' }}>{s.name} ({s.value}%)</span>
                </div>
              ))}
            </div>
        </div>
      </div>
    </div>
  );
}
